/**
 * useCaptureIntegrity — the customer-side capture-integrity (injection / virtual-camera) collection machine
 * (S5-4; AI_DESIGN §0.2 layer 2). The droom's companion to useGeoEvidence: it collects the client capture
 * fingerprint around getUserMedia and posts it to the session's injection-scan rail, so the officer's panel
 * carries a real INTERIM layer-2 signal.
 *
 * Honesty rules encoded here (mirroring useGeoEvidence):
 *  • Collect ONCE per live stream (keyed by the track). A re-acquired stream (facing switch / rejoin)
 *    collects again — that is a legitimate new attempt on the server's append-only ladder.
 *  • A collection/post failure NEVER blocks the capture flow — the customer continues; a missing injection
 *    signal is the officer's/gate's concern (fail-closed server-side), never a dead end for the customer.
 *  • Mock mode (no backend) is a no-op: no injection rail exists, so nothing is collected (data minimisation).
 *  • This runs BEST-EFFORT and off the critical path; it never stops the caller's tracks.
 *
 * The signal is PROBABILISTIC and client-reported — it does NOT prove camera provenance and never claims a
 * virtual camera is impossible. It is one layer of six; the hard guarantee is attested capture (roadmap).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isMockMode } from '@/api/client';
import {
  postInjectionFingerprint,
  type InjectionScanEvidence,
} from '@/api/injection';
import { collectCaptureFingerprint, newCaptureNonce } from './captureFingerprint';

/**
 * The shared collect-and-post core: sample the fingerprint from a live capture stream (fresh
 * nonce per attempt — every call is a distinct attempt on the server's append-only ladder) and
 * post it to the session's injection-scan rail. Used by the pre-call hook below AND the
 * live-call track lifecycle (`useLiveCall`, INTAKE-02 §2.7) — one collection logic, two
 * capture surfaces. Returns the echoed advisory row; null when there was nothing honest to
 * collect (no live video track / collector unavailable) or `isCancelled()` turned true between
 * sampling and posting (caller unmounted — nothing is posted). Post failures propagate so the
 * caller can stay fail-soft in its own idiom.
 */
export async function collectAndPostCaptureFingerprint(
  sessionId: string,
  stream: MediaStream,
  isCancelled: () => boolean = () => false,
): Promise<InjectionScanEvidence | null> {
  const fingerprint = await collectCaptureFingerprint(stream, { nonce: newCaptureNonce() });
  if (!fingerprint || isCancelled()) return null;
  return postInjectionFingerprint(sessionId, fingerprint);
}

export type CaptureIntegrityPhase =
  | 'idle' // no live stream to observe yet
  | 'collecting' // sampling the fingerprint + posting
  | 'captured' // posted (advisory echoed)
  | 'skipped' // mock mode / no video track — nothing to collect
  | 'failed'; // collection or post failed — non-blocking; the flow continues

export interface CaptureIntegrityState {
  readonly phase: CaptureIntegrityPhase;
  /** The advisory row the backend echoed (evidence for the officer; never gates the customer). */
  readonly evidence: InjectionScanEvidence | null;
  /** Force a re-collection (e.g. after a camera switch); appends a new attempt. */
  readonly recollect: () => void;
}

/**
 * @param sessionId the session to bind the scan to
 * @param getStream a stable getter for the current live capture stream (null until live)
 * @param enabled   true only while a live customer capture stream should be fingerprinted
 */
export function useCaptureIntegrity(
  sessionId: string,
  getStream: () => MediaStream | null,
  enabled: boolean,
): CaptureIntegrityState {
  const [phase, setPhase] = useState<CaptureIntegrityPhase>('idle');
  const [evidence, setEvidence] = useState<InjectionScanEvidence | null>(null);
  const disposed = useRef(false);
  /** The track we last collected for — collect once per distinct live track. */
  const collectedTrackId = useRef<string | null>(null);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  const collect = useCallback(async (): Promise<void> => {
    const stream = getStream();
    const track = stream?.getVideoTracks?.()[0];
    if (!stream || !track) {
      setPhase('idle');
      return;
    }
    if (isMockMode()) {
      setPhase('skipped'); // no rail to bind to — collect nothing (data minimisation)
      return;
    }
    if (collectedTrackId.current === track.id) return; // already collected this live track
    collectedTrackId.current = track.id;
    setPhase('collecting');
    try {
      const row = await collectAndPostCaptureFingerprint(
        sessionId,
        stream,
        () => disposed.current,
      );
      if (disposed.current) return;
      if (!row) {
        setPhase('skipped');
        return;
      }
      setEvidence(row);
      setPhase('captured');
    } catch {
      if (disposed.current) return;
      // Non-blocking: a failed injection collection is the gate's concern, never a dead end for the customer.
      setPhase('failed');
    }
  }, [sessionId, getStream]);

  useEffect(() => {
    if (!enabled) return;
    void collect();
  }, [enabled, collect]);

  const recollect = useCallback(() => {
    collectedTrackId.current = null;
    void collect();
  }, [collect]);

  return { phase, evidence, recollect };
}
